import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  runEnquirySiteVisitCancel,
  runEnquirySiteVisitComplete,
} from "./enquirySiteVisitWorkflow";

type EnquirySiteVisitWorkflowModalsProps = {
  visitId: number | null;
  completeOpen: boolean;
  cancelOpen: boolean;
  onCloseComplete: () => void;
  onCloseCancel: () => void;
  onSuccess: () => void | Promise<void>;
};

const EnquirySiteVisitWorkflowModals = ({
  visitId,
  completeOpen,
  cancelOpen,
  onCloseComplete,
  onCloseCancel,
  onSuccess,
}: EnquirySiteVisitWorkflowModalsProps) => {
  const [busy, setBusy] = useState(false);
  const [completeNotes, setCompleteNotes] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    if (!completeOpen) setCompleteNotes("");
  }, [completeOpen]);

  useEffect(() => {
    if (!cancelOpen) setCancelReason("");
  }, [cancelOpen]);

  const submitComplete = async () => {
    if (!visitId) return;
    setBusy(true);
    try {
      const ok = await runEnquirySiteVisitComplete(visitId, {
        description: completeNotes.trim() || undefined,
      });
      if (ok) {
        await onSuccess();
        onCloseComplete();
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (!visitId) return;
    setBusy(true);
    try {
      const ok = await runEnquirySiteVisitCancel(visitId, {
        cancellationReason: cancelReason.trim() || null,
      });
      if (ok) {
        await onSuccess();
        onCloseCancel();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal isOpen={completeOpen} toggle={() => !busy && onCloseComplete()}>
        <ModalHeader toggle={() => !busy && onCloseComplete()}>
          Complete site visit
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Marks the visit completed and records the time. You can add final
            notes below (optional).
          </p>
          <Label className="form-label">Notes (optional)</Label>
          <Input
            type="textarea"
            rows={3}
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
            placeholder="Summary or handover notes…"
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-soft-secondary"
            onClick={() => onCloseComplete()}
            disabled={busy}
          >
            Close
          </Button>
          <Button
            className="btn btn-soft-success"
            onClick={() => void submitComplete()}
            disabled={busy || !visitId}
          >
            {busy ? "Saving…" : "Mark complete"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={cancelOpen} toggle={() => !busy && onCloseCancel()}>
        <ModalHeader toggle={() => !busy && onCloseCancel()}>
          Cancel site visit
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            This cancels the visit. Add a reason if you want it on record.
          </p>
          <Label className="form-label">Reason (optional)</Label>
          <Input
            type="textarea"
            rows={2}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Why is this visit being cancelled?"
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-soft-secondary"
            onClick={() => onCloseCancel()}
            disabled={busy}
          >
            Back
          </Button>
          <Button
            className="btn btn-soft-danger"
            onClick={() => void submitCancel()}
            disabled={busy || !visitId}
          >
            {busy ? "Saving…" : "Cancel visit"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EnquirySiteVisitWorkflowModals;
